import { getAllowedDomains } from "app/containers/AdminConsole/helpers";
import { selectCompanySettings } from "app/containers/AdminConsole/slice";
import InviteUserForm from "app/storybookComponents/Forms/InviteUserForm";
import { selectHideInviteViaLinkOption } from "app/containers/Login/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  inviteUserByEmail,
  selectInviteUserByEmailStatus,
  selectAllCompanyUsersById,
  selectInvalidInvitedStrings,
  resetInvalidInvitedStrings,
} from "app/containers/Global/slice";
import { useEffect, useState } from "react";
import { selectUserIsAbleToInviteUsers } from "app/containers/UserGuide/slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getEmailManagerSettings,
  selectEmailManagerSettings,
} from "app/components/EmailManager/slice";

interface Props {
  onSuccessfulInvite: () => void;
  departmentId?: number | null;
  teamId?: number | null;
}

const InviteTeamLeader = ({
  onSuccessfulInvite,
  departmentId,
  teamId,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const companySettings = useAppSelector(selectCompanySettings);
  const usersInfoById = useAppSelector(selectAllCompanyUsersById);
  const inviteUsersByEmailStatus = useAppSelector(
    selectInviteUserByEmailStatus
  );
  const hideInviteViaLinkOption = useAppSelector(selectHideInviteViaLinkOption);
  const invalidInvitedStrings = useAppSelector(selectInvalidInvitedStrings);
  const canUserInviteNewMembers = useAppSelector(selectUserIsAbleToInviteUsers);
  const emailManagementSettings = useAppSelector(selectEmailManagerSettings);
  const [sendEmails, setSendEmails] = useState(
    !!emailManagementSettings?.inviteToOrganizationIsEnabled
  );
  // If no departmentId is passed then we need to show a dropdown to show all of the departments and allow the user to select one

  useEffect(() => {
    dispatch(getEmailManagerSettings());
    // Upon unmount we need to reset the invalid invited strings
    return () => {
      dispatch(resetInvalidInvitedStrings());
    };
  }, [dispatch]);

  useEffect(() => {
    setSendEmails(
      teamId
        ? !!emailManagementSettings?.teamInviteIsEnabled
        : !!emailManagementSettings?.inviteToOrganizationIsEnabled
    );
  }, [emailManagementSettings, teamId]);

  const onInviteViaEmail = async (emailAddresses: string[]) => {
    dispatch(resetInvalidInvitedStrings());
    const ti_onboardingRoleId = departmentId ? 3 : 2;

    // Create an async call to invite via email, upon a successful response show a modal or fire an event
    const resp = (await dispatch(
      inviteUserByEmail({
        emailAddresses,
        ti_onboardingRoleId,
        ti_onboardingDepartmentId: departmentId ?? undefined,
        teamId: teamId ?? undefined,
        sendInvitations: sendEmails,
      })
    )) as any;

    // If there was an error we should not call the onSuccessfulInvite function
    if (resp.error) return;
    onSuccessfulInvite();
  };

  const getAdditionalInviteViaEmailErrors = (
    selectedValues: { userAccountId?: number }[]
  ) => {
    const hasAnyUsersSelectedWhoAreAlreadyTeamLeaders = selectedValues.some(
      (userObj) => {
        const userAccountId = userObj.userAccountId;
        const user = userAccountId ? usersInfoById[userAccountId] : null;
        return user?.ti_onboardingRoleId === 2;
      }
    );
    if (hasAnyUsersSelectedWhoAreAlreadyTeamLeaders) {
      return (
        <div className="warning-banner light-red row-gap-12px align-items-center">
          <FontAwesomeIcon icon="triangle-exclamation" />
          <p>
            One or more of the team leaders you are trying to invite are already
            team leaders.
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <p className="mb-0">
        <b>Why add team leaders?</b> When you add team leaders, they can create
        teams, ensure their members take the TEAMscan survey, and receive
        teamwork insight reports for growth.
      </p>
      <InviteUserForm
        onInviteViaEmail={onInviteViaEmail}
        inviteMemberInfoMessage="Simply type in the email addresses of the team leaders you want to invite. They'll receive an invitation to join the platform."
        teamMembers={Object.values(usersInfoById)}
        allowedDomains={getAllowedDomains(companySettings)}
        isLoading={inviteUsersByEmailStatus === "loading"}
        invalidInvitedStrings={invalidInvitedStrings}
        onCSVUploadSuccess={() => {
          onSuccessfulInvite();
        }}
        hideCsvTab
        hideInviteViaLinkTab={
          !canUserInviteNewMembers || hideInviteViaLinkOption
        }
        resetInvalidInvitedStrings={() =>
          dispatch(resetInvalidInvitedStrings())
        }
        setSendEmailSettingProps={{
          setter: setSendEmails,
          value: sendEmails,
        }}
        getAdditionalInviteViaEmailErrors={getAdditionalInviteViaEmailErrors}
        checkEmailForDanger={(_, userAccountId) => {
          const user = userAccountId ? usersInfoById[userAccountId] : null;
          return user?.ti_onboardingRoleId === 2;
        }}
        entity="team leaders"
      />
    </>
  );
};

export default InviteTeamLeader;
